import {
  getContrastRatio,
  hexToRgb,
  parseColorToRgb,
  relativeLuminance,
} from "@/lib/colors";
import { useMemo } from "react";

// This function calculates the contrast ratio between two colors
// and determines the appropriate icon color (black or white) based on the luminance of the shade.
// It also checks if a border is needed based on the contrast ratio with the background color, to enure the shade stays visible.

export function useShadeContrast(
  shadeHex: string,
  containerEl?: HTMLElement | null,
): {
  iconColor: "black" | "white";
  needsBorder: boolean;
} {
  return useMemo(() => {
    const shadeRgb = hexToRgb(shadeHex);
    const shadeLum = relativeLuminance(shadeRgb);
    const iconColor = shadeLum < 0.5 ? "white" : "black"; // Slightly better threshold than 128

    let needsBorder = false;

    if (containerEl) {
      const styles = getComputedStyle(containerEl);
      const bgRaw = styles.getPropertyValue("--bg").trim();

      if (bgRaw) {
        try {
          const bgRgb = parseColorToRgb(bgRaw);
          if (bgRgb) {
            const bgLum = relativeLuminance(bgRgb);
            const contrast = getContrastRatio(shadeLum, bgLum);
            needsBorder = contrast < 2.5;
          }
        } catch (error) {
          console.warn(
            `[useShadeContrast] Failed to parse --bg value (${bgRaw}):`,
            error,
          );
          // Deafult to true to show a border to ensure shades are always visible
          needsBorder = true;
        }
      }
    }

    return { iconColor, needsBorder };
  }, [shadeHex, containerEl]);
}
